import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { 
  ArrowRight, 
  ArrowLeft, 
  Shield, 
  CheckCircle, 
  User, 
  Loader2,
  Sparkles,
  Lock,
  CreditCard,
  ExternalLink
} from 'lucide-react';

interface AccountSetupWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

type WizardStep = 'welcome' | 'profile' | 'payments' | 'complete';

export function AccountSetupWizard({ isOpen, onClose, onComplete }: AccountSetupWizardProps) {
  const [currentStep, setCurrentStep] = useState<WizardStep>('welcome');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    displayName: '',
    bio: '',
  });

  const steps: WizardStep[] = ['welcome', 'profile', 'payments', 'complete'];
  const stepIndex = steps.indexOf(currentStep);
  const progress = ((stepIndex + 1) / steps.length) * 100;

  useEffect(() => {
    // Pre-fill from user metadata if available
    const loadUserData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.user_metadata?.full_name) {
        setFormData(prev => ({
          ...prev,
          displayName: user.user_metadata.full_name || '',
        }));
      }
    };
    if (isOpen) loadUserData();
  }, [isOpen]);

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateProfileStep = (): boolean => {
    if (!formData.displayName.trim()) {
      toast.error('Please enter a display name');
      return false;
    }
    return true;
  };

  const handleNext = async () => {
    if (currentStep === 'welcome') {
      setCurrentStep('profile');
    } else if (currentStep === 'profile') {
      if (validateProfileStep()) {
        await saveProfile();
        setCurrentStep('payments');
      }
    } else if (currentStep === 'payments') {
      await completeSetup();
    }
  };

  const handleBack = () => {
    if (currentStep === 'profile') setCurrentStep('welcome');
    else if (currentStep === 'payments') setCurrentStep('profile');
  };

  const saveProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('profiles')
        .update({
          display_name: formData.displayName,
          bio: formData.bio,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) throw error;
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile');
    }
  };

  const completeSetup = async () => {
    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('onboarding_progress')
          .update({ 
            completed: true,
            steps_completed: ['welcome', 'profile', 'payments'],
          })
          .eq('user_id', user.id);
      }

      setCurrentStep('complete');
      toast.success('Account setup complete!');
    } catch (error) {
      console.error('Setup error:', error);
      toast.error('Failed to complete setup');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase
        .from('onboarding_progress')
        .update({ completed: true })
        .eq('user_id', user.id);
    }
    onClose();
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'welcome':
        return (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-chart-1/20 flex items-center justify-center">
                <Sparkles className="h-10 w-10 text-primary" />
              </div>
              <h3 className="text-xl font-semibold">Welcome to SwapX!</h3>
              <p className="text-muted-foreground">
                Let's get your account set up so you can start trading. This only takes about 1 minute.
              </p>
            </div>
            
            <div className="grid gap-3">
              <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg border">
                <Shield className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium">Secure Payments</h4>
                  <p className="text-sm text-muted-foreground">Pay securely with PayPal at checkout</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg border">
                <CreditCard className="h-5 w-5 text-chart-1 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium">Zero Custody</h4>
                  <p className="text-sm text-muted-foreground">SwapX never holds your funds - pay only after matching</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg border">
                <Lock className="h-5 w-5 text-chart-2 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium">Buyer Protection</h4>
                  <p className="text-sm text-muted-foreground">Funds held until delivery is confirmed</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'profile':
        return (
          <div className="space-y-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">Your Profile</h3>
                <p className="text-sm text-muted-foreground">How others will see you</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="displayName">Display Name *</Label>
              <Input
                id="displayName"
                placeholder="Your name or username"
                value={formData.displayName}
                onChange={(e) => updateField('displayName', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio (optional)</Label>
              <Input
                id="bio"
                placeholder="Tell us about yourself"
                value={formData.bio}
                onChange={(e) => updateField('bio', e.target.value)}
              />
            </div>

            <Alert className="bg-muted/50">
              <Lock className="h-4 w-4" />
              <AlertDescription className="text-xs">
                You can update your profile anytime from settings.
              </AlertDescription>
            </Alert>
          </div>
        );

      case 'payments':
        return (
          <div className="space-y-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">Payment Setup</h3>
                <p className="text-sm text-muted-foreground">How payments work on SwapX</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-muted/50 rounded-lg border">
                <h4 className="font-medium mb-2">🛒 Buying Items</h4>
                <p className="text-sm text-muted-foreground">
                  When your order matches, you'll pay securely via PayPal. No pre-funding required!
                </p>
              </div>

              <div className="p-4 bg-muted/50 rounded-lg border">
                <h4 className="font-medium mb-2">💰 Selling Items</h4>
                <p className="text-sm text-muted-foreground">
                  To receive payments, connect your PayPal account from the Sell page. 
                  This ensures funds go directly to you after delivery confirmation.
                </p>
              </div>

              <Alert>
                <Shield className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  All transactions are protected by PayPal's buyer and seller protection policies.
                </AlertDescription>
              </Alert>
            </div>
          </div>
        );

      case 'complete':
        return (
          <div className="flex flex-col items-center justify-center py-8 space-y-6">
            <div className="h-20 w-20 rounded-full bg-chart-2/20 flex items-center justify-center">
              <CheckCircle className="h-12 w-12 text-chart-2" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-2xl font-bold">You're All Set! 🎉</h3>
              <p className="text-muted-foreground">
                Your account is ready. You can now browse and buy items.
              </p>
            </div>

            <div className="w-full space-y-3">
              <Button className="w-full" onClick={() => { onComplete(); window.location.href = '/markets'; }}>
                Start Browsing
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button variant="outline" className="w-full" onClick={() => { onComplete(); window.location.href = '/sell'; }}>
                <ExternalLink className="mr-2 h-4 w-4" />
                Set Up Selling (Connect PayPal)
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-lg">Account Setup</DialogTitle>
          <DialogDescription>
            Step {stepIndex + 1} of {steps.length}
          </DialogDescription>
        </DialogHeader>

        <Progress value={progress} className="h-1" />

        <div className="py-4">
          {renderStep()}
        </div>

        {currentStep !== 'complete' && (
          <div className="flex justify-between pt-4 border-t">
            {currentStep === 'welcome' ? (
              <Button variant="ghost" onClick={handleSkip}>
                Skip for now
              </Button>
            ) : (
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            )}
            
            <Button onClick={handleNext} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : currentStep === 'payments' ? (
                'Complete Setup'
              ) : (
                <>
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
